import { useAppSelector } from "../app/hooks";
import { isDistrictOrSchoolAdmin } from "../app/userSlice";
import { Loading } from "../components/Loading";

/**
 * Should this route hold its children back until the server-side session's
 * school/district has been synced?
 *
 * This has to be answered during the guard's render pass, not from an effect:
 * react-router renders a layout and its <Outlet /> children in a single pass and
 * React runs child effects before parent effects, so by the time an effect could
 * react to the flag the children have already mounted and fired their queries.
 * Once a query is in flight, MakeSchoolSelection's "CurrentSchool" invalidation
 * can no longer repair it either - RTK Query resolves invalidations against
 * state.provided, which is only populated when a query fulfills - so the stale,
 * school-less response is what gets cached. Not rendering the children at all is
 * the only reliable fix.
 */
export function useAdminSessionGate() {
	const { role, isAdminSessionSynced } = useAppSelector((state) => state.user);

	// role === null means the role isn't known yet (cold browser, cleared local
	// storage, deep link). An unknown role could be an admin, so gate until GetSelf
	// resolves. Roles that need no session selection - super/state admins, and
	// course-scoped teachers and students - fall straight through.
	const needsSync = role === null || isDistrictOrSchoolAdmin(role);

	return needsSync && !isAdminSessionSynced;
}

/**
 * Placeholder rendered in place of an admin route's children while the gate holds.
 */
export function AdminSessionLoading() {
	return (
		<div className="flex items-center justify-center">
			<Loading loading={true} />
		</div>
	);
}
